import { storage } from './localStorage';

/**
 * The appearance dictionary as GET /appearance/schema returns it: key → the
 * plain values the admin offers for it, e.g. {align: ['left','center','right'],
 * color: ['primary', '#a81313', ...]}. Loaded once next to the applications and
 * cached the same way; the resolver does not need it (a colour value is a hex
 * or a Bootstrap name on its own), it is kept for anything that wants to offer
 * the admin's choices.
 */
export type AppearanceSchema = Record<string, string[]>;

const STORAGE_KEY = 'appearanceSchema';

let schema: AppearanceSchema | null = null;
let hydrated = false;

const isSchema = (value: unknown): value is AppearanceSchema =>
    !!value && typeof value === 'object' && !Array.isArray(value)
    && Object.values(value as Record<string, unknown>).every(Array.isArray);

/** Remembers the schema for the session and for the next page load. */
export const setAppearanceSchema = (value: AppearanceSchema | null): void => {
    schema = isSchema(value) ? value : null;
    hydrated = true;
    try {
        if (schema) {
            storage.setItem(STORAGE_KEY, JSON.stringify(schema));
        } else {
            storage.removeItem(STORAGE_KEY);
        }
    } catch {
        // localStorage unavailable or full - the session copy still works
    }
};

/** The schema, or null before the first load; a page reload reuses the cached copy. */
export const getAppearanceSchema = (): AppearanceSchema | null => {
    if (!hydrated) {
        hydrated = true;
        try {
            const cached = storage.getItem(STORAGE_KEY);
            const parsed = cached ? JSON.parse(cached) : null;
            schema = isSchema(parsed) ? parsed : null;
        } catch {
            schema = null;
        }
    }
    return schema;
};

/** A value that is a colour of its own, or null for a name the theme has to resolve. */
export const appearanceHex = (value: string): string | null =>
    /^#[0-9a-f]{6}$/i.test(value) ? value : null;
